/**
 * Adoption and benefits (research audit item 4) and the organisation's AI readiness report
 * (item 11). Not mirrored in the workbench: both are answers the platform works out from what it
 * already holds (the fleet, runs, boards, plans, pull requests, approvals and the usage ledger).
 *
 *   GET /admin/v1/adoption?days=30                          administrators and portfolio managers
 *   GET /admin/v1/adoption/statement?month=YYYY-MM&format=  the same numbers for a calendar month and the one before
 *   GET /admin/v1/readiness                                 administrators
 *   GET /admin/v1/readiness/report?format=md|html           the readiness answer as a document
 */
import { z } from "zod";
export const ADOPTION_MAX_DAYS = 90;
export const adoptionQuerySchema = z.object({
    /** The window, in whole days ending now; 30 when left out. */
    days: z.coerce.number().int().min(1).max(ADOPTION_MAX_DAYS).default(30),
});
export const adoptionStatementQuerySchema = z.object({
    /** YYYY-MM, UTC; the month just ended when left out. */
    month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/).optional(),
    format: z.enum(["json", "csv"]).default("json"),
});
export const adoptionSummarySchema = z.object({
    window: z.object({ from: z.string(), to: z.string(), days: z.number() }),
    people: z.object({ members: z.number(), activeWeek: z.number(), activeWindow: z.number() }),
    machines: z.object({ enrolled: z.number(), reportedWindow: z.number(), online: z.number() }),
    agents: z.object({
        runs: z.number(),
        runsEnded: z.number(),
        byOutcome: z.object({ ok: z.number(), error: z.number(), cancelled: z.number() }),
        teamsUsed: z.number(),
        specialistsUsed: z.number(),
    }),
    work: z.object({
        tasksDone: z.number(),
        plansDone: z.number(),
        pullRequestsMerged: z.number(),
        approvalsWaited: z.object({ count: z.number(), medianMinutes: z.number().optional() }),
    }),
    cost: z.object({
        /** Metered spend in `currency` (the organisation's currency; US dollars when it has none set). */
        total: z.number(),
        currency: z.string(),
        totalUsd: z.number(),
        perTaskDone: z.number().optional(),
        perActivePerson: z.number().optional(),
    }),
    trend: z.array(z.object({ day: z.string(), runs: z.number(), tasksDone: z.number(), activePeople: z.number() })),
});
export const adoptionStatementSchema = z.object({
    month: z.string(),
    organisation: z.string(),
    generatedAt: z.string(),
    current: adoptionSummarySchema,
    previous: adoptionSummarySchema,
});
/** The statement as CSV: one row per measure, the month, the month before and the change. */
export function adoptionStatementCsv(s) {
    const cell = (v) => (v === undefined ? "" : typeof v === "number" ? String(v) : /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
    const row = (...cells) => cells.map(cell).join(",");
    const c = s.current;
    const p = s.previous;
    const change = (a, b) => (a === undefined || b === undefined ? undefined : Math.round((a - b) * 1_000_000) / 1_000_000);
    const measures = [
        ["people_members", c.people.members, p.people.members],
        ["people_active_window", c.people.activeWindow, p.people.activeWindow],
        ["machines_enrolled", c.machines.enrolled, p.machines.enrolled],
        ["machines_reporting", c.machines.reportedWindow, p.machines.reportedWindow],
        ["agent_runs", c.agents.runs, p.agents.runs],
        ["agent_runs_ended", c.agents.runsEnded, p.agents.runsEnded],
        ["agent_runs_ok", c.agents.byOutcome.ok, p.agents.byOutcome.ok],
        ["agent_runs_error", c.agents.byOutcome.error, p.agents.byOutcome.error],
        ["agent_runs_cancelled", c.agents.byOutcome.cancelled, p.agents.byOutcome.cancelled],
        ["teams_used", c.agents.teamsUsed, p.agents.teamsUsed],
        ["specialists_used", c.agents.specialistsUsed, p.agents.specialistsUsed],
        ["tasks_done", c.work.tasksDone, p.work.tasksDone],
        ["plans_done", c.work.plansDone, p.work.plansDone],
        ["pull_requests_merged", c.work.pullRequestsMerged, p.work.pullRequestsMerged],
        ["approvals_waited", c.work.approvalsWaited.count, p.work.approvalsWaited.count],
        ["approval_wait_median_minutes", c.work.approvalsWaited.medianMinutes, p.work.approvalsWaited.medianMinutes],
        [`cost_total_${c.cost.currency.toLowerCase()}`, c.cost.total, p.cost.total],
        ["cost_total_usd", c.cost.totalUsd, p.cost.totalUsd],
        ["cost_per_task_done", c.cost.perTaskDone, p.cost.perTaskDone],
        ["cost_per_active_person", c.cost.perActivePerson, p.cost.perActivePerson],
    ];
    const prevMonth = s.previous.window.from.slice(0, 7);
    return [row("measure", s.month, prevMonth, "change"), ...measures.map(([m, a, b]) => row(m, a, b, change(a, b)))].join("\n") + "\n";
}
// ── The organisation's AI readiness ────────────────────────────────────
export const readinessQuerySchema = z.object({ format: z.enum(["md", "html"]).default("html") });
export const readinessGapSchema = z.object({ area: z.string(), sentence: z.string(), severity: z.enum(["high", "medium", "low"]) });
export const readinessSchema = z.object({
    generatedAt: z.string(),
    organisation: z.string(),
    fleet: z.object({ workbenches: z.number(), nodes: z.number(), online: z.number(), versions: z.array(z.object({ version: z.string(), count: z.number() })) }),
    policy: z.object({ policies: z.number(), effectiveDefault: z.boolean(), clausesSet: z.array(z.string()) }),
    tools: z.object({
        governed: z.number(),
        askFirst: z.number(),
        off: z.number(),
        ungovernedSeen: z.number(),
        /** Policies by their `tools.afterOutside` clause; `enforcedOnRecord`: those enforcing tools, unchanged for 30 days or more, that only record outside content. */
        afterOutside: z.object({ off: z.number(), record: z.number(), ask: z.number(), enforcedOnRecord: z.number() }),
    }),
    dlp: z.object({ mode: z.enum(["off", "classifier", "purview", "both"]), classifierNodes: z.number() }),
    identities: z.object({ agentIdentities: z.number(), registered: z.number(), problems: z.number() }),
    classification: z.object({ projects: z.number(), byLevel: z.object({ public: z.number(), internal: z.number(), confidential: z.number(), restricted: z.number().optional(), unset: z.number() }) }),
    approvals: z.object({ open: z.number(), oldestMinutes: z.number().optional() }),
    adoption: adoptionSummarySchema,
    gaps: z.array(readinessGapSchema),
});
//# sourceMappingURL=adoption.js.map